import React, { useState, useEffect } from 'react';
import { getArchivedTenders, getArchiveYears } from '../services/api';
import { useToast } from '../context/ToastContext';
import { formatDate } from '../utils/formatDate';
import './TenderList.css';

function ArchivedTenderList() {
  const [tenders, setTenders] = useState([]);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const toast = useToast();

  const fetchYears = async () => {
    try {
      const res = await getArchiveYears();
      if (res.success) setYears(res.years || []);
    } catch {
      // ignore
    }
  };

  const fetchTenders = async (year) => {
    try {
      setLoading(true);
      const res = await getArchivedTenders(year || null);
      if (res.success) setTenders(res.tenders || []);
    } catch (err) {
      toast.error('Failed to load archived tenders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchYears();
    fetchTenders(selectedYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleYearChange = (e) => {
    const yr = e.target.value;
    setSelectedYear(yr);
    fetchTenders(yr);
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getSourceClass = (source) => {
    const sourceClasses = {
      gem: 'source-gem',
      cppp: 'source-cppp',
      state_portal: 'source-state',
      direct: 'source-direct',
      other: 'source-other',
    };
    return sourceClasses[source] || 'source-other';
  };

  return (
    <div className="tender-list-section">
      <div className="list-header">
        <h2>Archived Tenders</h2>
      </div>

      <div className="archive-year-filter">
        <label>Filter by year:</label>
        <select value={selectedYear} onChange={handleYearChange}>
          <option value="">All Years</option>
          {years.map((yr) => (
            <option key={yr} value={yr}>{yr}</option>
          ))}
        </select>
        {!loading && (
          <span style={{ fontSize: '13px', color: '#6b7280' }}>
            {tenders.length} tender{tenders.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {loading ? (
        <div className="loading-state">Loading archived tenders...</div>
      ) : tenders.length === 0 ? (
        <div className="empty-state">
          <p>{selectedYear ? `No archived tenders for ${selectedYear}` : 'No archived tenders yet'}</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="tenders-table">
            <thead>
              <tr>
                <th>Tender Ref</th>
                <th>Title</th>
                <th>Source</th>
                <th>Buyer</th>
                <th>Won Value</th>
                <th>Tender Value</th>
                <th>Archived</th>
                <th>Year</th>
              </tr>
            </thead>
            <tbody>
              {tenders.map((t) => (
                <React.Fragment key={t.id}>
                  <tr
                    className={`tender-row ${expandedId === t.id ? 'expanded' : ''}`}
                    onClick={() => setExpandedId(expandedId === t.id ? null : t.id)}
                  >
                    <td><strong>{t.tender_ref}</strong></td>
                    <td>{t.title || '-'}</td>
                    <td>
                      <span className={`source-badge ${getSourceClass(t.source)}`}>
                        {t.source_display || t.source}
                      </span>
                    </td>
                    <td>{t.buyer_name || '-'}</td>
                    <td>{formatCurrency(t.won_value)}</td>
                    <td>{formatCurrency(t.tender_value)}</td>
                    <td className="archive-meta">{formatDate(t.archived_at)}</td>
                    <td><span className="archived-badge">{t.archive_year}</span></td>
                  </tr>
                  {expandedId === t.id && (
                    <tr className="tender-detail-row">
                      <td colSpan="8">
                        <div className="tender-expanded-details">
                          <div className="tender-details-grid">
                            <div className="detail-item">
                              <label>Buyer / Department</label>
                              <span>{t.buyer_name || '-'}</span>
                            </div>
                            <div className="detail-item">
                              <label>Submission Deadline</label>
                              <span>{formatDate(t.submission_deadline)}</span>
                            </div>
                            <div className="detail-item">
                              <label>Estimated Value</label>
                              <span>{formatCurrency(t.tender_value)}</span>
                            </div>
                            <div className="detail-item">
                              <label>Won Value</label>
                              <span>{formatCurrency(t.won_value)}</span>
                            </div>
                            <div className="detail-item">
                              <label>EMD Amount</label>
                              <span>{formatCurrency(t.emd_amount)}</span>
                            </div>
                            <div className="detail-item">
                              <label>Go Ahead Date</label>
                              <span>{formatDate(t.go_ahead_date)}</span>
                            </div>
                            <div className="detail-item">
                              <label>Products</label>
                              <span>{(t.products && t.products.length) || 0} items</span>
                            </div>
                            <div className="detail-item">
                              <label>Archived By</label>
                              <span>{t.archived_by || '-'}</span>
                            </div>
                          </div>
                          {t.description && (
                            <p className="tender-description-snippet">{t.description}</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ArchivedTenderList;
